import React, { useState, useEffect, useCallback, useRef } from 'react';
import styles from './ForagingMinigame.module.css';
import { ItemData } from '../data/ItemData';
import { gameConfigData } from '../data/data';

const FORAGING_PHASES = {
  IDLE: 'idle',
  STARTING: 'starting',
  ACTIVE: 'active',
  RESULT: 'result',
};

const ForagingMinigame = ({ playerForagingSkill, playerData, onGameEnd }) => {
  const [phase, setPhase] = useState(FORAGING_PHASES.IDLE);
  const [message, setMessage] = useState('');
  const [timeLeft, setTimeLeft] = useState(8);
  const [activeTargets, setActiveTargets] = useState([]);
  const [foundItems, setFoundItems] = useState([]);
  const [dangerousClicks, setDangerousClicks] = useState(0);
  const [totalClicks, setTotalClicks] = useState(0);
  
  const gameTimerRef = useRef(null);
  const countdownRef = useRef(null);
  const spawnTimerRef = useRef(null);
  const phaseRef = useRef(FORAGING_PHASES.IDLE);
  const targetIdCounter = useRef(0);
  const sessionIdRef = useRef(null);
  
  // Add refs to track current values for game end
  const foundItemsRef = useRef([]);
  const dangerousClicksRef = useRef(0);
  const totalClicksRef = useRef(0);

  // Update phase ref whenever phase changes
  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  // Update refs when state changes
  useEffect(() => {
    foundItemsRef.current = foundItems;
  }, [foundItems]);

  useEffect(() => {
    dangerousClicksRef.current = dangerousClicks;
  }, [dangerousClicks]);

  useEffect(() => {
    totalClicksRef.current = totalClicks;
  }, [totalClicks]);

  // Fast-paced game settings
  const gameSettings = React.useMemo(() => ({
    gameDuration: 8000, // 8 seconds total
    spawnInterval: 300, // New targets spawn every 300ms
    targetLifetime: 1500, // Targets live for 1.5 seconds
    maxActiveTargets: 6, // Up to 6 targets visible at once
    maxDangerousClicks: 2, // Game ends if you click 2 dangerous items
  }), []);

  // Available forageable items - heavily weighted toward dangerous items
  const availableItems = React.useMemo(() => {
    const baseItems = [
      // Good items (rare!)
      { key: 'berries', name: 'Berries', type: 'good', weight: 8, img: '/img/consumables/berries.png' },
      { key: 'herbs', name: 'Herbs', type: 'good', weight: 6, img: '/img/crafting/herbs.png' },
      { key: 'wildMushroom', name: 'Wild Mushroom', type: 'good', weight: 4, img: '/img/crafting/wildMushroom.png' },
      { key: 'shinyPebble', name: 'Shiny Pebble', type: 'good', weight: 2, img: '/img/crafting/shinyPebble.png' },
      
      // Dangerous items (very common!)
      { key: 'poisonousPlant', name: 'Poisonous Plant', type: 'dangerous', weight: 20, img: '/img/crafting/poisonGland.png' },
      { key: 'thornyBush', name: 'Thorny Bush', type: 'dangerous', weight: 18, img: '/img/crafting/bone.png' },
      { key: 'deadlyMushroom', name: 'Deadly Mushroom', type: 'dangerous', weight: 16, img: '/img/crafting/poisonPotion.png' },
      { key: 'rottenBerries', name: 'Rotten Berries', type: 'dangerous', weight: 14, img: '/img/loot/moldy_berries_icon.png' },
      { key: 'venomousSpider', name: 'Venomous Spider', type: 'dangerous', weight: 12, img: '/img/crafting/spiderLeg.png' },
    ];
    
    // Only add seeds that have been unlocked
    const unlockedSeeds = playerData?.unlockedSeeds || [];
    const allSeeds = [
      { key: 'carrotSeed', name: 'Carrot Seed', type: 'good', weight: 3, img: '/img/farming/seeds/carrot_seed.png' },
      { key: 'cucumberSeed', name: 'Cucumber Seed', type: 'good', weight: 3, img: '/img/farming/seeds/cucumber_seed.png' },
      { key: 'beetSeed', name: 'Beet Seed', type: 'good', weight: 3, img: '/img/farming/seeds/beet_seed.png' },
      { key: 'onionSeed', name: 'Onion Seed', type: 'good', weight: 3, img: '/img/farming/seeds/onion_seed.png' },
      { key: 'garlicSeed', name: 'Garlic Seed', type: 'good', weight: 3, img: '/img/farming/seeds/garlic_seed.png' },
      { key: 'kaleSeed', name: 'Kale Seed', type: 'good', weight: 3, img: '/img/farming/seeds/kale_seed.png' },
      { key: 'greenbeanSeed', name: 'Green Bean Seed', type: 'good', weight: 2, img: '/img/farming/seeds/greenbean_seed.png' },
      { key: 'tomatoSeed', name: 'Tomato Seed', type: 'good', weight: 2, img: '/img/farming/seeds/tomato_seed.png' },
      { key: 'pepperSeed', name: 'Pepper Seed', type: 'good', weight: 2, img: '/img/farming/seeds/pepper_seed.png' },
      { key: 'eggplantSeed', name: 'Eggplant Seed', type: 'good', weight: 2, img: '/img/farming/seeds/eggplant_seed.png' },
      { key: 'cauliflowerSeed', name: 'Cauliflower Seed', type: 'good', weight: 2, img: '/img/farming/seeds/cauliflower_seed.png' },
      { key: 'pumpkinSeed', name: 'Pumpkin Seed', type: 'good', weight: 1, img: '/img/farming/seeds/pumpkin_seed.png' },
    ];
    
    // Filter seeds to only include unlocked ones
    const unlockedSeedItems = allSeeds.filter(seed => unlockedSeeds.includes(seed.key));
    
    return [...baseItems, ...unlockedSeedItems];
  }, [playerData?.unlockedSeeds]);

  const resetGameState = useCallback(() => {
    setPhase(FORAGING_PHASES.IDLE);
    setMessage('');
    setTimeLeft(8);
    setActiveTargets([]);
    setFoundItems([]);
    setDangerousClicks(0);
    setTotalClicks(0);
    targetIdCounter.current = 0;
    
    // Reset refs
    foundItemsRef.current = [];
    dangerousClicksRef.current = 0;
    totalClicksRef.current = 0;
    
    // Clear all timers
    if (gameTimerRef.current) clearTimeout(gameTimerRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    if (spawnTimerRef.current) clearInterval(spawnTimerRef.current);
  }, []);

  // Weighted random selection for items
  const getWeightedRandomItem = useCallback(() => {
    const totalWeight = availableItems.reduce((sum, item) => sum + item.weight, 0);
    const randomValue = Math.random() * totalWeight;
    
    let cumulativeWeight = 0;
    for (const item of availableItems) {
      cumulativeWeight += item.weight;
      if (randomValue <= cumulativeWeight) {
        return item;
      }
    }
    
    return availableItems[0];
  }, [availableItems]);

  const generateRandomTarget = useCallback(() => {
    const gridSize = 8; // 8x8 grid for more positions
    const positions = [];
    
    for (let row = 0; row < gridSize; row++) {
      for (let col = 0; col < gridSize; col++) {
        positions.push({
          x: (col * 12.5) + 6.25, // Percentage positions for 8x8 grid
          y: (row * 12.5) + 6.25,
          gridId: `${row}-${col}`
        });
      }
    }
    
    // Find positions not currently occupied
    const occupiedPositions = activeTargets.map(target => target.gridId);
    const availablePositions = positions.filter(pos => !occupiedPositions.includes(pos.gridId));
    
    if (availablePositions.length === 0) return null;
    
    const randomPosition = availablePositions[Math.floor(Math.random() * availablePositions.length)];
    const randomItem = getWeightedRandomItem();
    
    return {
      id: targetIdCounter.current++,
      ...randomPosition,
      ...randomItem,
      spawnTime: Date.now(),
    };
  }, [activeTargets, getWeightedRandomItem]);

  const endGame = useCallback((failed = false) => {
    // Clear all timers first
    if (spawnTimerRef.current) {
      clearInterval(spawnTimerRef.current);
      spawnTimerRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    if (gameTimerRef.current) {
      clearTimeout(gameTimerRef.current);
      gameTimerRef.current = null;
    }
    
    setPhase(FORAGING_PHASES.RESULT);
    
    if (failed) {
      setMessage('💀 You touched too many dangerous plants! No rewards!');
      setTimeout(() => {
        onGameEnd({ 
          outcome: 'failed', 
          reason: 'Too many dangerous items clicked',
          foundItems: [], // No rewards when failed!
          totalClicks: totalClicksRef.current,
          sessionId: sessionIdRef.current
        });
      }, 1500);
    } else {
      setMessage('⏰ Time\'s up! Here\'s what you safely gathered!');
      setTimeout(() => {
        onGameEnd({ 
          outcome: 'success', 
          foundItems: foundItemsRef.current, // Use ref for current values
          totalClicks: totalClicksRef.current,
          sessionId: sessionIdRef.current
        });
      }, 1500);
    }
  }, [onGameEnd]);

  const spawnTargets = useCallback(() => {
    if (phaseRef.current !== FORAGING_PHASES.ACTIVE) return;
    
    setActiveTargets(prev => {
      // Remove expired targets
      const now = Date.now();
      const alive = prev.filter(target => 
        now - target.spawnTime < gameSettings.targetLifetime
      );
      
      // Add new targets - spawn 2-3 at a time for chaos!
      const newTargets = [...alive];
      const targetsToSpawn = Math.min(
        gameSettings.maxActiveTargets - newTargets.length,
        Math.random() < 0.6 ? 3 : 2 // 60% chance to spawn 3, 40% chance to spawn 2
      );
      
      for (let i = 0; i < targetsToSpawn; i++) {
        const newTarget = generateRandomTarget();
        if (newTarget) {
          newTargets.push(newTarget);
        }
      }
      
      return newTargets;
    });
  }, [generateRandomTarget, gameSettings.targetLifetime, gameSettings.maxActiveTargets]);

  const handleTargetClick = useCallback((targetId, event) => {
    event.stopPropagation();
    
    setActiveTargets(prev => {
      const targetIndex = prev.findIndex(target => target.id === targetId);
      if (targetIndex === -1) return prev;
      
      const clickedTarget = prev[targetIndex];
      
      setTotalClicks(prevTotal => {
        const newTotal = prevTotal + 1;
        totalClicksRef.current = newTotal; // Keep ref in sync
        return newTotal;
      });
      
      if (clickedTarget.type === 'dangerous') {
        // Clicked a dangerous item!
        setDangerousClicks(prevDangerous => {
          const newDangerousCount = prevDangerous + 1;
          dangerousClicksRef.current = newDangerousCount; // Keep ref in sync
          if (newDangerousCount >= gameSettings.maxDangerousClicks) {
            // Game over - no rewards!
            endGame(true); // true = failed due to dangerous clicks
          }
          return newDangerousCount;
        });
      } else {
        // Clicked a good item!
        setFoundItems(prevItems => {
          const newItems = [...prevItems, clickedTarget];
          foundItemsRef.current = newItems; // Keep ref in sync
          return newItems;
        });
      }
      
      // Remove the clicked target
      return prev.filter(target => target.id !== targetId);
    });
  }, [gameSettings.maxDangerousClicks, endGame]);

  const startForaging = useCallback(() => {
    console.log('[ForagingMinigame] Starting foraging frenzy game');
    resetGameState();
    setPhase(FORAGING_PHASES.STARTING);
    setMessage('🌿 Get ready for foraging madness! Avoid red/dangerous items - 2 strikes and you lose everything!');
    
    // Generate unique session ID
    sessionIdRef.current = `foraging_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    setTimeout(() => {
      setPhase(FORAGING_PHASES.ACTIVE);
      setMessage('🏃‍♀️ GO! Click green items, avoid red danger! Quick decisions!');
      
      // Start countdown timer
      setTimeLeft(8);
      countdownRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            clearInterval(countdownRef.current);
            countdownRef.current = null;
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      // Start target spawning
      spawnTimerRef.current = setInterval(spawnTargets, gameSettings.spawnInterval);
      
      // End game after 8 seconds
      gameTimerRef.current = setTimeout(() => {
        endGame(false); // false = time ended, not failed
      }, gameSettings.gameDuration);
      
      // Spawn initial targets
      spawnTargets();
    }, 2000);
  }, [resetGameState, gameSettings, spawnTargets, endGame]);

  useEffect(() => {
    startForaging();
    
    return () => {
      if (gameTimerRef.current) clearTimeout(gameTimerRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
      if (spawnTimerRef.current) clearInterval(spawnTimerRef.current);
    };
  }, []); // Empty dependency array - only run once on mount

  const renderGameArea = () => {
    return (
      <div className={styles.forestArea}>
        {activeTargets.map((target) => (
          <div
            key={target.id}
            className={`${styles.forageableItem} ${target.type === 'dangerous' ? styles.dangerousItem : styles.goodItem}`}
            style={{
              left: `${target.x}%`,
              top: `${target.y}%`,
              transform: 'translate(-50%, -50%)',
              backgroundImage: `url(${target.img})`,
            }}
            onClick={(event) => handleTargetClick(target.id, event)}
            title={target.name}
          />
        ))}
      </div>
    );
  };

  // Count items by type for display
  const goodItemCount = foundItems.length;

  return (
    <div className={styles.foragingOverlay}>
      <div className={styles.foragingWindow}>
        <h2 className={styles.title}>Foraging Frenzy!</h2>
        
        <div className={styles.messageArea}>
          {message}
        </div>
        
        {phase === FORAGING_PHASES.ACTIVE && (
          <div className={styles.gameStats}>
            <div className={styles.timer}>
              <span className={styles.timerLabel}>Time:</span>
              <span className={styles.timerValue}>{timeLeft}s</span>
            </div>
            <div className={styles.clickCounter}>
              <span className={styles.clickLabel}>Good Items:</span>
              <span className={styles.clickValue}>{goodItemCount}</span>
            </div>
            <div className={styles.dangerCounter}>
              <span className={styles.dangerLabel}>Danger Clicks:</span>
              <span className={`${styles.dangerValue} ${dangerousClicks >= 1 ? styles.warning : ''}`}>
                {dangerousClicks}/2
              </span>
            </div>
          </div>
        )}
        
        {phase === FORAGING_PHASES.RESULT && (
          <div className={styles.resultsArea}>
            <h3>Foraging Results:</h3>
            {foundItems.length > 0 ? (
              <div className={styles.itemResults}>
                {foundItems.map((item, index) => (
                  <div key={index} className={styles.itemResult}>
                    <img src={item.img} alt={item.name} className={styles.itemResultIcon} />
                    <span>{item.name}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className={styles.noRewards}>No items gathered this time!</p>
            )}
            <p className={styles.totalSummary}>
              Total clicks: {totalClicks} | Safe items: {goodItemCount} | Dangerous hits: {dangerousClicks}
            </p>
          </div>
        )}
        
        {phase === FORAGING_PHASES.ACTIVE && renderGameArea()}
        
        <div className={styles.actions}>
          <button
            onClick={() => onGameEnd({ outcome: 'cancelled', sessionId: sessionIdRef.current })}
            className={`${styles.actionButton} ${styles.cancelButton}`}
          >
            {phase === FORAGING_PHASES.RESULT ? 'Close' : 'Cancel'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ForagingMinigame; 